import { useEffect, useMemo, useRef, useState } from "react";
import { StatusIcon } from "./statusIcon.tsx";

export interface IdentitySinglePickerConfig<T> {
  items: T[];
  getId: (item: T) => number;
  /** Property key on the item used as the primary display name (e.g. "StoreName"). */
  nameField: keyof T & string;
  /** Substring match against `lower` (already lowercased). Should include ID-as-string. */
  matchFn: (item: T, lower: string) => boolean;
  /** Optional secondary info line rendered under the name (e.g. "City, State, Zip"). */
  secondaryFn?: (item: T) => string;
  /** Singular noun used in placeholders ("location", "kiosk"). */
  noun: string;
  /** Max suggestions to render — keeps the popover snappy on multi-thousand-item caches. */
  maxSuggestions?: number;
  /** Max rows listed when the list is opened with nothing typed (dropdown mode). Default: all. */
  maxBrowse?: number;
}

interface IdentitySinglePickerProps<T> {
  /** Current selection. `0` (or negative) = unset. */
  value: number;
  onChange: (id: number) => void;
  config: IdentitySinglePickerConfig<T>;
  /** Override placeholder for the search input (defaults to `Search {noun}…`). */
  placeholder?: string;
  disabled?: boolean;
  /** Called when the user presses Enter inside the ID input — use to submit a form. */
  onIdEnter?: () => void;
  /** Fires whenever the typed search text changes — hosts that want to mirror
   *  the same filter onto a sibling list (e.g. configured-items table)
   *  subscribe here. ID-input edits do NOT fire this. */
  onSearchChange?: (text: string) => void;
}

/**
 * Single-select identity picker (ID + search, inline style).
 *
 * Mirrors the visual language of the DataGrid `InlineVariant` identity filter
 * but emits a single numeric ID instead of `InputParam[]`, and drops the
 * multi-value bulk-select UI. Reusable anywhere a form needs "pick one
 * location / kiosk / product / merchandiser" — callers provide items +
 * match/display config.
 */
export function IdentitySinglePicker<T>({
  value,
  onChange,
  config,
  placeholder,
  disabled,
  onIdEnter,
  onSearchChange,
}: IdentitySinglePickerProps<T>) {
  const [idText, setIdText] = useState(value > 0 ? String(value) : "");
  const [search, setSearch] = useState("");
  const [showSuggest, setShowSuggest] = useState(false);
  const [highlighted, setHighlighted] = useState(0);
  const rootRef = useRef<HTMLDivElement>(null);

  /* Debounce upstream onChange for the ID input only. Without this, typing
   * "46" first commits "4" (firing parent fetches keyed on endpoint 4 etc.)
   * then "46". The debounce keeps the visible text immediate but waits for
   * the user to pause before propagating. Enter/blur flush eagerly. */
  const ID_DEBOUNCE_MS = 300;
  const idTimerRef = useRef<number | null>(null);
  const pendingIdRef = useRef<number | null>(null);

  // Keep the ID text in sync when the parent overwrites the value.
  useEffect(() => {
    setIdText(value > 0 ? String(value) : "");
  }, [value]);

  useEffect(() => () => {
    if (idTimerRef.current != null) window.clearTimeout(idTimerRef.current);
  }, []);

  const flushIdCommit = () => {
    if (idTimerRef.current != null) {
      window.clearTimeout(idTimerRef.current);
      idTimerRef.current = null;
    }
    if (pendingIdRef.current == null) return;
    const next = pendingIdRef.current;
    pendingIdRef.current = null;
    onChange(next);
  };

  // Resolve the current selection for the placeholder hint.
  const current = useMemo(() => {
    if (!value) return null;
    return config.items.find((i) => config.getId(i) === value) ?? null;
  }, [value, config]);

  const currentName = current ? String(current[config.nameField] ?? "") : "";

  // Nothing typed = dropdown: list every item, oldest first by id, so the one
  // created last is at the bottom of the scroll (customers look for the kiosk or
  // location they just made; a 300 cap hid it). Typing filters as before.
  const browsing = !search.trim();
  const browseMax = config.maxBrowse ?? Number.POSITIVE_INFINITY;
  const browseList = useMemo<T[]>(
    () => [...config.items].sort((a, b) => config.getId(a) - config.getId(b)),
    [config],
  );
  const suggestions = useMemo<T[]>(() => {
    const q = search.trim().toLowerCase();
    if (!q) return browseMax < browseList.length ? browseList.slice(0, browseMax) : browseList;
    const max = config.maxSuggestions ?? 30;
    const out: T[] = [];
    for (const it of config.items) {
      if (config.matchFn(it, q)) {
        out.push(it);
        if (out.length >= max) break;
      }
    }
    return out;
  }, [search, config, browseMax, browseList]);

  // Close suggestions on outside click.
  useEffect(() => {
    if (!showSuggest) return;
    function handler(e: MouseEvent) {
      if (!rootRef.current) return;
      if (rootRef.current.contains(e.target as Node)) return;
      setShowSuggest(false);
    }
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, [showSuggest]);

  const commit = (id: number) => {
    onChange(id);
    setIdText(id > 0 ? String(id) : "");
    setSearch("");
    onSearchChange?.("");
    setShowSuggest(false);
    setHighlighted(0);
  };

  const handleIdChange = (raw: string) => {
    const clean = raw.replace(/[^0-9]/g, "");
    setIdText(clean);
    if (clean === "") {
      // Clearing fires immediately — no debounce window needed.
      pendingIdRef.current = null;
      if (idTimerRef.current != null) { window.clearTimeout(idTimerRef.current); idTimerRef.current = null; }
      onChange(0);
      return;
    }
    const n = Number(clean);
    if (!Number.isFinite(n) || n <= 0) return;
    pendingIdRef.current = n;
    if (idTimerRef.current != null) window.clearTimeout(idTimerRef.current);
    idTimerRef.current = window.setTimeout(flushIdCommit, ID_DEBOUNCE_MS);
  };

  const handleSearchKey = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "ArrowDown") {
      e.preventDefault();
      if (!showSuggest) { setShowSuggest(true); return; }
      setHighlighted((h) => Math.min(h + 1, Math.max(0, suggestions.length - 1)));
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setHighlighted((h) => Math.max(h - 1, 0));
    } else if (e.key === "Enter") {
      const pick = showSuggest ? suggestions[highlighted] : undefined;
      if (pick) { e.preventDefault(); commit(config.getId(pick)); }
    } else if (e.key === "Escape") {
      setShowSuggest(false);
    }
  };

  const searchPlaceholder = placeholder ?? (currentName ? currentName : `Search ${config.noun}…`);

  return (
    <div
      ref={rootRef}
      style={{
        display: "inline-flex",
        alignItems: "stretch",
        border: "1px solid var(--color-border-default)",
        borderRadius: "var(--radius-1)",
        background: "var(--color-bg)",
        width: "100%",
        position: "relative",
        opacity: disabled ? 0.6 : 1,
      }}
    >
      <div
        style={{
          display: "flex",
          alignItems: "center",
          padding: "var(--space-1) var(--space-1) 0 var(--space-2)",
          color: "var(--color-text-muted)",
          borderRight: "1px solid var(--color-border-default)",
          fontSize: "var(--text-xs)",
          fontWeight: 600,
          letterSpacing: "0.04em",
        }}
      >
        ID
      </div>
      <input
        value={idText}
        disabled={disabled}
        onChange={(e) => handleIdChange(e.target.value)}
        onKeyDown={(e) => {
          if (e.key === "Enter") {
            flushIdCommit();
            if (onIdEnter) onIdEnter();
          }
        }}
        onBlur={flushIdCommit}
        placeholder="—"
        aria-label={`${config.noun} ID`}
        style={{
          width: 90,
          border: "none",
          padding: "0 var(--space-2)",
          height: 28,
          font: "inherit",
          fontSize: "var(--text-sm)",
          outline: "none",
          fontVariantNumeric: "tabular-nums",
          background: "transparent",
          color: "var(--color-text)",
        }}
      />
      <div
        style={{
          position: "relative",
          flex: 1,
          minWidth: 0,
          borderLeft: "1px solid var(--color-border-default)",
          display: "flex",
          alignItems: "center",
        }}
      >
        <span
          aria-hidden="true"
          style={{
            padding: "0 var(--space-0-5) 0 var(--space-2)",
            color: "var(--color-text-muted)",
            display: "inline-flex",
            alignItems: "center",
          }}
        >
          <SearchIcon />
        </span>
        <input
          value={search}
          disabled={disabled}
          onChange={(e) => { setSearch(e.target.value); onSearchChange?.(e.target.value); setShowSuggest(true); setHighlighted(0); }}
          onFocus={() => { setShowSuggest(true); setHighlighted(0); }}
          onClick={() => setShowSuggest(true)}
          role="combobox"
          aria-expanded={showSuggest}
          aria-autocomplete="list"
          onKeyDown={handleSearchKey}
          placeholder={searchPlaceholder}
          aria-label={`Search ${config.noun}`}
          style={{
            width: "100%",
            minWidth: 0,
            border: "none",
            padding: "0 var(--space-2) 0 var(--space-1)",
            height: 28,
            font: "inherit",
            fontSize: "var(--text-sm)",
            outline: "none",
            background: "transparent",
            color: "var(--color-text)",
          }}
        />
        {showSuggest && suggestions.length > 0 && (
          <div
            role="listbox"
            style={{
              position: "absolute",
              top: "calc(100% + 4px)",
              left: -1,
              right: -1,
              zIndex: 10,
              background: "var(--color-bg)",
              border: "1px solid var(--color-border-default)",
              borderRadius: "var(--radius-1)",
              boxShadow: "var(--shadow-md)",
              maxHeight: 280,
              overflowY: "auto",
            }}
          >
            {browsing && config.items.length > browseMax && (
              <div
                style={{
                  padding: "var(--space-1) var(--space-3)",
                  fontSize: "var(--text-xs)",
                  color: "var(--color-text-muted)",
                  background: "var(--color-bg-muted)",
                  borderBottom: "1px solid var(--color-border-default)",
                }}
              >
                First {browseMax} of {config.items.length} — type to filter
              </div>
            )}
            {suggestions.map((item, idx) => (
              <div
                key={config.getId(item)}
                role="option"
                aria-selected={config.getId(item) === value}
                onMouseDown={(e) => e.preventDefault()}
                onMouseEnter={() => setHighlighted(idx)}
                onClick={() => commit(config.getId(item))}
                style={{
                  padding: "var(--space-1-5) var(--space-3)",
                  cursor: "pointer",
                  background: idx === highlighted ? "var(--color-bg-muted)" : undefined,
                  borderBottom: "1px solid var(--color-border-default)",
                  fontWeight: config.getId(item) === value ? 600 : undefined,
                }}
              >
                <div style={{ display: "flex", gap: "var(--space-2)", alignItems: "baseline" }}>
                  <span
                    style={{
                      fontSize: "var(--text-xs)",
                      color: "var(--color-text-muted)",
                      fontVariantNumeric: "tabular-nums",
                      minWidth: 32,
                    }}
                  >
                    #{config.getId(item)}
                  </span>
                  <span style={{ color: "var(--color-text)" }}>
                    {String(item[config.nameField] ?? "") || "(no name)"}
                  </span>
                </div>
                {config.secondaryFn && (
                  <div style={{ fontSize: "var(--text-xs)", color: "var(--color-text-muted)", marginLeft: 40 }}>
                    {config.secondaryFn(item)}
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </div>
      {!disabled && (
        <button
          type="button"
          tabIndex={-1}
          onMouseDown={(e) => e.preventDefault()}
          onClick={() => setShowSuggest((o) => !o)}
          aria-label={showSuggest ? `Close ${config.noun} list` : `Show all ${config.noun}s`}
          title={showSuggest ? "Close list" : `Show all ${config.noun}s`}
          style={{
            background: "transparent",
            border: "none",
            borderLeft: "1px solid var(--color-border-default)",
            padding: "0 var(--space-2)",
            cursor: "pointer",
            color: "var(--color-text-muted)",
            display: "inline-flex",
            alignItems: "center",
          }}
        >
          <StatusIcon kind="chevronDown" size={14} />
        </button>
      )}
      {value > 0 && !disabled && (
        <button
          type="button"
          onClick={() => commit(0)}
          aria-label="Clear"
          title="Clear"
          style={{
            borderLeft: "1px solid var(--color-border-default)",
            background: "transparent",
            border: "none",
            borderLeftWidth: 1,
            borderLeftStyle: "solid",
            borderLeftColor: "var(--color-border-default)",
            padding: "0 var(--space-2)",
            cursor: "pointer",
            color: "var(--color-text-muted)",
            fontSize: "var(--text-base)",
            lineHeight: 1,
          }}
        >
          ×
        </button>
      )}
    </div>
  );
}

function SearchIcon() {
  return <StatusIcon kind="search" size={13} />;
}
